import { useState, useEffect, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ChartCard } from "./chart-card";
import { Button } from "@/components/ui/button";
import {
	BarChart,
	Bar,
	XAxis,
	YAxis,
	CartesianGrid,
	Tooltip,
	ResponsiveContainer,
	Legend,
} from "recharts";
import { BAR_VALUE_LABEL, NO_ANIMATION } from "./chart-ticks";
import { Users, Calendar, Download } from "lucide-react";
import { useAuth } from "@/global-hooks/useAuth";
import { downloadChartPng } from "./utils";
import { toAPIFilter } from "./utils";
import type { StatisticsFilters } from "./StatisticsControls";
import statisticsAPIService, { type AgeDistributionData } from "@/lib/statistics-api.service";

interface AgeDistributionTabProps {
	/** Bộ lọc của cả trang (khoa, khoá, khoảng ngày). Thiếu nó thì mục này
	 * hiện số của mọi khoá ngay cạnh các tab đã lọc. */
	filters?: StatisticsFilters;
	/** Tăng lên mỗi lần trang yêu cầu tải lại; đổi giá trị là fetch lại. */
	refreshToken?: number;
}

export default function AgeDistributionTab({ filters, refreshToken = 0 }: AgeDistributionTabProps) {
	const ageRef = useRef<HTMLDivElement>(null);
	const { user } = useAuth();
	const [ageStats, setAgeStats] = useState<AgeDistributionData[]>([]);
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);

	const canExport = user?.role === "admin" || user?.role === "manager";

	useEffect(() => {
		const fetchAgeStats = async () => {
			try {
				setIsLoading(true);
				const data = await statisticsAPIService.getAgeDistribution(toAPIFilter(filters, "total"));
				setAgeStats(data);
			} catch (err) {
				console.error("Error fetching age distribution:", err);
				setError(err instanceof Error ? err.message : "Failed to load age distribution");
			} finally {
				setIsLoading(false);
			}
		};

		fetchAgeStats();
		// `refreshToken`: nút Tải lại của trang phải làm mới cả mục này.
	}, [refreshToken, filters]);

	const totalStudents = ageStats.reduce((sum, item) => sum + item.total, 0);
	const avgAge =
		ageStats.length > 0
			? ageStats.reduce((sum, item) => {
					// Calculate midpoint age for each group
					let midAge = 0;
					switch (item.ageGroup) {
						case "Dưới 18":
							midAge = 17;
							break;
						case "18-20":
							midAge = 19;
							break;
						case "21-22":
							midAge = 21.5;
							break;
						case "23-25":
							midAge = 24;
							break;
						case "Trên 25":
							midAge = 27;
							break;
					}
					return sum + midAge * item.total;
				}, 0) / totalStudents
			: 0;

	const chartData = ageStats.map((item) => ({
		name: item.ageGroup,
		"Tổng số": item.total,
		Nam: item.male,
		Nữ: item.female,
		percentage: item.percentage,
	}));

	// Chỉ chiếm chỗ khi chưa có gì để hiện. Lần tải lại sau đó giữ nguyên biểu đồ
	// cũ thay vì thay nó bằng một dòng chữ rồi dựng lại từ đầu.
	if (isLoading && ageStats.length === 0) {
		return (
			<div className="space-y-6">
				<div className="flex items-center justify-center p-8">
					<div className="text-center">
						<div className="animate-pulse text-lg">Đang tải thống kê phân bố tuổi...</div>
					</div>
				</div>
			</div>
		);
	}

	if (error) {
		return (
			<div className="space-y-6">
				<div className="flex items-center justify-center p-8">
					<div className="text-center text-red-600 dark:text-red-400">
						<div className="text-lg font-medium">Lỗi tải dữ liệu</div>
						<div className="text-sm">{error}</div>
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className="space-y-6">
			<div ref={ageRef}>
				<div className="flex items-center justify-between mb-6">
					<div className="flex items-center gap-3">
						<Calendar className="h-6 w-6 text-blue-600 dark:text-blue-400" />
						<div>
							<h2 className="text-2xl font-bold">Phân bố theo Tuổi</h2>
							<p className="text-muted-foreground">Thống kê độ tuổi sinh viên</p>
						</div>
					</div>

					<div className="flex items-center gap-2">
						{canExport && (
							<Button
								onClick={() => {
									// Export logic would go here
									console.log("Export age distribution as CSV");
								}}
								variant="outline"
								size="sm"
								className="flex items-center gap-2"
							>
								<Download className="h-4 w-4" />
								Tải CSV
							</Button>
						)}
						<Button
							onClick={() => downloadChartPng(ageRef, "phan-bo-theo-tuoi")}
							variant="outline"
							size="sm"
							className="flex items-center gap-2"
						>
							<Download className="h-4 w-4" />
							Tải PNG
						</Button>
					</div>
				</div>

				{/* Summary Cards */}
				<div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
					<Card>
						<CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
							<CardTitle className="text-sm font-medium">Tổng sinh viên</CardTitle>
							<Users className="h-4 w-4 text-blue-600 dark:text-blue-400" />
						</CardHeader>
						<CardContent>
							<div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
								{totalStudents.toLocaleString()}
							</div>
							<p className="text-xs text-muted-foreground">Có thông tin tuổi</p>
						</CardContent>
					</Card>

					<Card>
						<CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
							<CardTitle className="text-sm font-medium">Tuổi trung bình</CardTitle>
							<Calendar className="h-4 w-4 text-green-700 dark:text-green-400" />
						</CardHeader>
						<CardContent>
							<div className="text-2xl font-bold text-green-700 dark:text-green-400">
								{avgAge.toFixed(1)} tuổi
							</div>
							<p className="text-xs text-muted-foreground">Ước tính từ nhóm tuổi</p>
						</CardContent>
					</Card>

					<Card>
						<CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
							<CardTitle className="text-sm font-medium">Nhóm phổ biến nhất</CardTitle>
							<Users className="h-4 w-4 text-purple-600 dark:text-purple-400" />
						</CardHeader>
						<CardContent>
							<div className="text-2xl font-bold text-purple-600 dark:text-purple-400">
								{ageStats.length > 0
									? ageStats.reduce((max, item) => (item.total > max.total ? item : max)).ageGroup
									: "N/A"}
							</div>
							<p className="text-xs text-muted-foreground">
								{ageStats.length > 0
									? `${ageStats.reduce((max, item) => (item.total > max.total ? item : max)).percentage.toFixed(1)}% sinh viên`
									: ""}
							</p>
						</CardContent>
					</Card>
				</div>

				{/* Age Distribution Chart */}
				<ChartCard
					title="Biểu đồ phân bố tuổi"
					filename="bieu-do-phan-bo-tuoi"
					icon={<BarChart className="h-5 w-5" />}
					className="mb-6"
				>
					<div className="h-80">
						<ResponsiveContainer width="100%" height="100%">
							<BarChart data={chartData}>
								<CartesianGrid strokeDasharray="3 3" />
								<XAxis dataKey="name" />
								<YAxis />
								<Tooltip
									formatter={(value: number, name: string) => [value.toLocaleString(), name]}
									labelFormatter={(label) => `Nhóm tuổi: ${label}`}
								/>
								<Legend />
								<Bar
									{...NO_ANIMATION}
									dataKey="Tổng số"
									fill="#3b82f6"
									radius={[4, 4, 0, 0]}
									label={BAR_VALUE_LABEL}
								/>
								<Bar
									{...NO_ANIMATION}
									dataKey="Nam"
									fill="#10b981"
									radius={[4, 4, 0, 0]}
									label={BAR_VALUE_LABEL}
								/>
								<Bar
									{...NO_ANIMATION}
									dataKey="Nữ"
									fill="#f59e0b"
									radius={[4, 4, 0, 0]}
									label={BAR_VALUE_LABEL}
								/>
							</BarChart>
						</ResponsiveContainer>
					</div>
				</ChartCard>

				{/* Detailed Table */}
				<Card>
					<CardHeader>
						<CardTitle>Chi tiết phân bố tuổi</CardTitle>
					</CardHeader>
					<CardContent>
						<div className="overflow-x-auto">
							<table className="w-full border-collapse">
								<thead>
									<tr className="border-b">
										<th className="text-left p-3 font-medium">Nhóm tuổi</th>
										<th className="text-right p-3 font-medium">Tổng số</th>
										<th className="text-right p-3 font-medium">Nam</th>
										<th className="text-right p-3 font-medium">Nữ</th>
										<th className="text-right p-3 font-medium">Tỷ lệ</th>
									</tr>
								</thead>
								<tbody>
									{ageStats.map((item, index) => (
										<tr key={index} className="border-b hover:bg-accent">
											<td className="p-3 font-medium">{item.ageGroup}</td>
											<td className="p-3 text-right">{item.total.toLocaleString()}</td>
											<td className="p-3 text-right text-blue-600 dark:text-blue-400">
												{item.male.toLocaleString()}
											</td>
											<td className="p-3 text-right text-pink-600 dark:text-pink-400">
												{item.female.toLocaleString()}
											</td>
											<td className="p-3 text-right font-medium">{item.percentage.toFixed(1)}%</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>
					</CardContent>
				</Card>
			</div>
		</div>
	);
}
